/**
 * Admin login screen (admin-arch §7.2, design §6.18).
 *
 * States covered:
 *  - loading      → submit button busy, form locked
 *  - empty (idle) → blank form
 *  - error        → inline Mantine alert for: bad credentials (401),
 *                   2FA required, 2FA not configured, rate-limit (429),
 *                   server-side failure (5xx), network failure
 *  - success      → navigate to `return_to` (validated by `safeReturnTo`)
 *
 * Authenticated users that land here are bounced to the safe return path —
 * fix for review finding Major-4.
 *
 * Wave 2.54 FE (TASK-295) — Google sign-in removed. The MVP auth surface is
 * email + password (+ 2FA) only; backend Google OAuth controller dropped in
 * TASK-293. Any `?google_error=` query left in old bookmarks is ignored — we
 * no longer surface the alert because the corresponding handler is gone.
 */

import { useEffect, useMemo, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import {
  Alert,
  Anchor,
  Button,
  Center,
  Loader,
  PasswordInput,
  Stack,
  Text,
  TextInput,
} from '@mantine/core';
import { useForm } from '@mantine/form';
import { zodResolver } from 'mantine-form-zod-resolver';
import { loginInputSchema, type LoginInput } from '@music-app/shared/schemas/auth';
import { useAuth, useLogin } from '@/hooks/use-auth';
import { ApiError } from '@/lib/api-client';
import { AuthPageShell } from '@/pages/auth/AuthPageShell';
import { loginNoticeFromState } from '@/pages/auth/auth-forms';
import { safeReturnTo } from '@/lib/session-expired-modal';

type FormStage = 'credentials' | 'totp' | 'recovery';

interface AlertState {
  color: 'red' | 'yellow' | 'blue';
  title?: string;
  message: string;
}

function describeError(err: ApiError): { alert: AlertState; stage?: FormStage } {
  const env = err.envelope ?? {};
  if (err.status === 401) {
    if (env.requires_2fa) {
      return {
        alert: {
          color: 'blue',
          title: 'Требуется код 2FA',
          message: 'Введите 6-значный код из приложения-аутентификатора.',
        },
        stage: 'totp',
      };
    }
    // TASK-450: the API stopped emitting this the moment 2FA became voluntary
    // (CEO 2026-07-27). Kept as a defensive fallback — the SPA is mounted
    // out-of-band from the API container, so a rolling deploy can briefly pair
    // this bundle with an older API that still short-circuits here. Copy now
    // points at the self-service page instead of the old "ask an admin" advice.
    if (env.requires_2fa_setup) {
      return {
        alert: {
          color: 'yellow',
          title: '2FA не настроен',
          message:
            'Войдите ещё раз через минуту — панель обновляется. Настроить 2FA можно позже в «Настройки → Безопасность».',
        },
      };
    }
    if (err.code === 'INVALID_2FA_CODE') {
      return {
        alert: {
          color: 'red',
          message: 'Неверный код 2FA. Проверьте время на телефоне и попробуйте ещё раз.',
        },
        stage: 'totp',
      };
    }
    if (err.code === 'INVALID_RECOVERY_CODE') {
      return {
        alert: {
          color: 'red',
          message: 'Резервный код не подошёл. Каждый код работает только один раз.',
        },
        stage: 'recovery',
      };
    }
    return {
      alert: {
        color: 'red',
        message: 'Неверный email или пароль.',
      },
    };
  }
  if (err.status === 403 && err.code === 'CLIENT_ROLE_NOT_ALLOWED') {
    return {
      alert: {
        color: 'red',
        title: 'Доступ запрещён',
        message:
          'Этот раздел только для сотрудников musichead и селекторов. Клиентам — клиентское приложение.',
      },
    };
  }
  if (err.status === 429) {
    const retry = err.retryAfter;
    const tail = retry ? ` Попробуйте через ${retry} с.` : ' Попробуйте позже.';
    return {
      alert: {
        color: 'red',
        title: 'Слишком много попыток',
        message: `Превышен лимит попыток входа.${tail}`,
      },
    };
  }
  if (err.status >= 500) {
    return {
      alert: {
        color: 'red',
        title: 'Сервер недоступен',
        message: 'Не удалось связаться с сервером. Попробуйте позже.',
      },
    };
  }
  return {
    alert: {
      color: 'red',
      message: err.envelope?.error?.message ?? 'Не удалось войти. Попробуйте ещё раз.',
    },
  };
}

export function LoginPage() {
  const { isAuthenticated, isBootstrapping } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const login = useLogin();
  const [stage, setStage] = useState<FormStage>('credentials');
  const [alert, setAlert] = useState<AlertState | null>(null);

  const returnTo = useMemo(() => {
    const params = new URLSearchParams(location.search);
    return safeReturnTo(params.get('return_to'));
  }, [location.search]);

  // Handed over by the accept-invite / reset-password redirects (TASK-449).
  const notice = useMemo(() => loginNoticeFromState(location.state), [location.state]);

  const form = useForm<LoginInput>({
    mode: 'uncontrolled',
    initialValues: {
      email: '',
      password: '',
      totp_code: undefined,
      recovery_code: undefined,
    },
    validate: zodResolver(loginInputSchema),
  });

  // Major-4: bounce authenticated users out of the login page.
  useEffect(() => {
    if (!isBootstrapping && isAuthenticated) {
      navigate(returnTo, { replace: true });
    }
  }, [isBootstrapping, isAuthenticated, navigate, returnTo]);

  if (isBootstrapping) {
    return (
      <Center mih="100dvh">
        <Loader aria-label="Проверяем сессию…" />
      </Center>
    );
  }
  if (isAuthenticated) {
    return null;
  }

  const onSubmit = (values: LoginInput) => {
    setAlert(null);
    // Exactly one second factor travels per submit — the backend prefers
    // `totp_code` when both are present, so leaking a stale one across stages
    // would silently shadow the code the user just typed.
    const payload: LoginInput = {
      email: values.email,
      password: values.password,
      ...(stage === 'totp' && values.totp_code ? { totp_code: values.totp_code } : {}),
      ...(stage === 'recovery' && values.recovery_code
        ? { recovery_code: values.recovery_code }
        : {}),
    };
    login.mutate(payload, {
      onSuccess: () => {
        navigate(returnTo, { replace: true });
      },
      onError: (err) => {
        const { alert: nextAlert, stage: nextStage } = describeError(err);
        setAlert(nextAlert);
        if (nextStage) setStage(nextStage);
      },
    });
  };

  return (
    <AuthPageShell subtitle="Войдите, чтобы продолжить работу с админ-панелью.">
      <form onSubmit={form.onSubmit(onSubmit)} noValidate>
        <Stack gap="md">
          {notice && !alert ? (
            <Alert color="teal" variant="light" role="status">
              {notice}
            </Alert>
          ) : null}

          {alert ? (
            <Alert color={alert.color} title={alert.title} variant="light" role="alert">
              {alert.message}
            </Alert>
          ) : null}

          <TextInput
            label="Email"
            placeholder="you@example.com"
            type="email"
            autoComplete="email"
            required
            disabled={login.isPending}
            key={form.key('email')}
            {...form.getInputProps('email')}
          />

          <PasswordInput
            label="Пароль"
            placeholder="Не короче 8 символов"
            autoComplete="current-password"
            required
            disabled={login.isPending}
            key={form.key('password')}
            {...form.getInputProps('password')}
          />

          {stage === 'totp' ? (
            <>
              <TextInput
                label="Код 2FA"
                placeholder="123456"
                inputMode="numeric"
                autoComplete="one-time-code"
                maxLength={6}
                required
                disabled={login.isPending}
                key={form.key('totp_code')}
                {...form.getInputProps('totp_code')}
              />
              <Text size="xs" c="dimmed">
                Нет доступа к телефону?{' '}
                <Anchor
                  component="button"
                  type="button"
                  size="xs"
                  onClick={() => {
                    form.setFieldValue('totp_code', undefined);
                    setAlert(null);
                    setStage('recovery');
                  }}
                >
                  Использовать резервный код
                </Anchor>
              </Text>
            </>
          ) : null}

          {stage === 'recovery' ? (
            <>
              <TextInput
                label="Резервный код"
                placeholder="ABCD-EF12-3456"
                autoComplete="one-time-code"
                maxLength={32}
                required
                disabled={login.isPending}
                key={form.key('recovery_code')}
                {...form.getInputProps('recovery_code')}
                onChange={(event) =>
                  form.setFieldValue(
                    'recovery_code',
                    event.currentTarget.value.toUpperCase(),
                  )
                }
              />
              <Text size="xs" c="dimmed">
                Каждый код работает один раз.{' '}
                <Anchor
                  component="button"
                  type="button"
                  size="xs"
                  onClick={() => {
                    form.setFieldValue('recovery_code', undefined);
                    setAlert(null);
                    setStage('totp');
                  }}
                >
                  Ввести код из приложения
                </Anchor>
              </Text>
            </>
          ) : null}

          <Button type="submit" loading={login.isPending} fullWidth>
            Войти
          </Button>

          <Text size="xs" c="dimmed" ta="center">
            Забыли пароль?{' '}
            <Anchor component={Link} to="/forgot-password" size="xs">
              Восстановить
            </Anchor>
          </Text>
        </Stack>
      </form>
    </AuthPageShell>
  );
}
